import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LockIcon, ShieldAlertIcon } from 'lucide-react';
import { DexButton } from '@dexmate/dexui';
import { ApiError } from '@/api/client';

/**
 * AuthBanner — global banner shown when any TanStack Query failure has
 * status 401 or 403. Clicking the action focuses the role dropdown
 * (handled via a DOM event the AppHeader listens for) so the user can
 * pick a different role without opening DevTools.
 *
 * Auto-dismisses on the next successful fetch because we read the live
 * query cache rather than tracking a separate piece of state.
 */
export function AuthBanner() {
  const qc = useQueryClient();
  const queries = qc.getQueryCache().getAll();

  const offending = useMemo(() => {
    for (const q of queries) {
      const err = q.state.error as ApiError | Error | null | undefined;
      if (err && err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        return err;
      }
    }
    return null;
    // The dependency on `queries` would re-run every render anyway; that's
    // fine — this is a cheap O(n) scan over a small cache and lets the
    // banner reflect the latest cache state without a separate subscription.
  }, [queries]);

  if (!offending) return null;

  const isAuth = offending.status === 401;
  const tone = isAuth ? 'border-dex-status-warning bg-dex-status-warning-bg' : 'border-dex-status-error bg-dex-status-error-bg';
  const fg = isAuth ? 'text-dex-status-warning' : 'text-dex-status-error';
  const Icon = isAuth ? LockIcon : ShieldAlertIcon;
  const headline = isAuth
    ? 'Token not recognised'
    : 'This token is missing the scope this page needs';

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`flex items-start gap-3 border-b ${tone} px-6 py-3`}
    >
      <Icon aria-hidden className={`mt-0.5 size-4 shrink-0 ${fg}`} />
      <div className="min-w-0 flex-1 text-sm">
        <div className={`font-semibold ${fg}`}>{headline}</div>
        <div className="text-dex-text-secondary">
          {offending.message || (isAuth ? 'Sign in to continue.' : 'Switch to a role with the required scope.')}
        </div>
      </div>
      <DexButton
        variant="secondary"
        onClick={() =>
          window.dispatchEvent(new CustomEvent('cds:open-role-dropdown'))
        }
      >
        Switch role
      </DexButton>
    </div>
  );
}
